import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Grid, Typography, Box, Paper, Button, styled, alpha } from '@mui/material';
import { keyframes } from '@mui/system';
import { ViewQuilt, Security, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';

// <<< MUDANÇA: Importando o hook de autenticação
import { useAuth } from '../context/AuthContext'; // Ajuste o caminho se necessário

// ============================================================================
// COMPONENTES ESTILIZADOS (Sem alterações)
// ============================================================================

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const FeatureCard = styled(Paper)(({ theme, delay = 0, glowColor }) => ({
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(4),
    height: '100%',
    width: '100%',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)',
    transition: 'transform 0.3s ease, box-shadow 0.4s ease',
    animation: `${fadeInUp} 0.6s ease-out forwards`,
    animationDelay: `${delay}s`,
    opacity: 0,
    border: '1px solid #E5E7EB',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle at 50% 0, ${alpha(glowColor, 0.15)}, transparent 40%)`,
        opacity: 0,
        transition: 'opacity 0.4s ease',
    },
    '&:hover': {
        transform: 'translateY(-12px)',
        boxShadow: `0 20px 40px ${alpha(glowColor, 0.2)}`,
        '&:before': {
            opacity: 1,
        }
    },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
    width: 64,
    height: 64,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
    color: '#FFFFFF',
    background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
    boxShadow: `0 6px 12px ${alpha(color, 0.3)}`,
    fontSize: '32px',
}));

// ============================================================================
// COMPONENTE DA PÁGINA DASHBOARD
// ============================================================================

const DashboardPage = () => {
    const navigate = useNavigate();
    
    // <<< MUDANÇA: Usando o hook para obter os dados do usuário
    const { user } = useAuth();

    const modules = [
        {
            icon: <ViewQuilt sx={{ fontSize: 'inherit' }} />,
            color: '#2563EB',
            glowColor: '#2563EB',
            title: 'Canvas Business Model',
            description: 'Acesse a ferramenta para criar, visualizar e editar seus Modelos de Negócio.',
            delay: 0.2,
            path: '/canvases' 
        },
        {
            icon: <Security sx={{ fontSize: 'inherit' }} />,
            color: '#D97706',
            glowColor: '#D97706',
            title: 'Canvas Business Model Risk Map',
            description: 'Analise e mapeie os riscos estratégicos associados ao seu Modelo de Negócio.',
            delay: 0.3,
            path: '/riskmap'
        },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                <Typography
                    variant="h3" component="h1"
                    sx={{ fontWeight: 800, color: '#111827', animation: `${fadeInUp} 0.5s ease-out`, mb: 1 }}
                >
                    Desenvolvimento do Modelo de Negócio
                </Typography>
                <Typography
                    variant="h6" component="p"
                    sx={{ color: '#6B7280', fontWeight: 400, maxWidth: '750px', mx: 'auto', animation: `${fadeInUp} 0.5s ease-out 0.1s`, opacity: 0, animationFillMode: 'forwards' }}
                >
                    {/* <<< MUDANÇA: Exibindo o nome do usuário com estilo */}
                    Bem-vindo(a), <Box component="span" sx={{ color: 'primary.main', fontWeight: '600' }}>{user?.name || 'Usuário'}</Box>! Selecione uma das ferramentas abaixo para iniciar sua análise estratégica.
                </Typography>
            </Box>

           <Grid container spacing={4} justifyContent="center">
                {modules.map((mod) => (
                    <Grid item xs={12} sm={6} md={5} lg={4} key={mod.title} sx={{ display: 'flex' }}> 
                        <FeatureCard
                            elevation={0}
                            delay={mod.delay}
                            glowColor={mod.glowColor}
                            onClick={() => navigate(mod.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <IconWrapper color={mod.color}>{mod.icon}</IconWrapper>
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#1F2937', mb: 1.5 }}>{mod.title}</Typography>
                                <Typography variant="body2" sx={{ color: '#6B7280', flexGrow: 1, minHeight: '40px' }}>{mod.description}</Typography>
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{ mt: 3, fontWeight: 600, color: mod.color, '&:hover': { backgroundColor: alpha(mod.color, 0.08) } }}
                                >
                                    Acessar Ferramenta
                                </Button>
                            </Box>
                        </FeatureCard>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default DashboardPage;